<article
    class="booking-area-card"
    [class.booking-area-card--unavailable]="!hasSelectableSlots()"
    data-testid="booking-area-card"
>
    @if (group().area; as area) {
        @if (canCollapse()) {
            <button
                type="button"
                class="booking-area-card-header w-100 d-flex align-items-center justify-content-between gap-3 text-start"
                [id]="cardId() + '-toggle'"
                [attr.aria-controls]="cardId() + '-slots'"
                [attr.aria-expanded]="isExpanded()"
                data-testid="booking-area-card-toggle"
                (click)="toggle()"
            >
                <span class="vstack gap-1 justify-content-center align-items-start flex-grow-1">
                    <span class="h6 fw-semibold m-0">{{ area.displayName }}</span>
                    @if (area.description; as description) {
                        <span class="small text-muted">{{ description }}</span>
                    }
                </span>
                <i
                    class="booking-area-card-chevron bi bi-chevron-down flex-shrink-0"
                    [class.booking-area-card-chevron--expanded]="isExpanded()"
                    aria-hidden="true"
                ></i>
            </button>
        } @else {
            <header
                class="booking-area-card-header vstack gap-1"
                [id]="cardId() + '-heading'"
            >
                <h3 class="m-0">
                    <span class="h6 fw-semibold m-0">{{ area.displayName }}</span>
                </h3>
                @if (area.description; as description) {
                    <p class="small mb-0">{{ description }}</p>
                }
            </header>
        }
    }

    @if (isExpanded()) {
        <div
            class="booking-area-card-slots vstack gap-3"
            [class.booking-area-card-slots--without-header]="group().area === null"
            [id]="cardId() + '-slots'"
            [attr.role]="group().area ? 'region' : null"
            [attr.aria-labelledby]="group().area
                ? cardId() + (canCollapse() ? '-toggle' : '-heading')
                : null"
        >
            @if (!hasSelectableSlots()) {
                <div
                    class="booking-area-no-availability vstack gap-2 text-center fw-semibold"
                    data-testid="no-availability-message"
                >
                    @if (group().noAvailabilityDescription; as description) {
                        <span [innerHTML]="description" class="no-availability-description"></span>
                    } @else {
                        Sorry, we don't have any tables available for your party size at this time.
                    }
                    @if (group().canJoinWaitlist) {
                        <div class="mt-1">
                            <button
                                type="button"
                                class="btn btn-outline-dark btn-sm"
                                data-testid="join-waitlist-button"
                                (click)="joinWaitlistRequested.emit()"
                            >
                                Join the waitlist
                            </button>
                        </div>
                    }
                </div>
            } @else if (group().slots.length > 0) {
                <div
                    class="booking-slot-grid"
                    role="list"
                    [attr.aria-label]="hasWaitlistSlots()
                        ? 'Available and waitlist booking times'
                        : 'Available booking times'"
                >
                    @for (slot of visibleSlots(); track slot.bookingSlot.dateTime.getTime()) {
                        <div class="booking-slot-grid-item" role="listitem">
                            <button
                                type="button"
                                class="booking-slot-button btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2"
                                [class.active]="isSelected(slot)"
                                [class.booking-slot-button--waitlist]="isWaitlistOnly(slot)"
                                [class.booking-slot-button--unavailable]="!isSelectable(slot)"
                                [disabled]="!isSelectable(slot)"
                                [attr.aria-pressed]="isSelected(slot)"
                                [attr.data-testid]="isReservable(slot)
                                    ? 'booking-slot'
                                    : isWaitlistOnly(slot)
                                        ? 'waitlist-booking-slot'
                                        : 'unavailable-booking-slot'"
                                (click)="selectSlot(slot)"
                            >
                                @if (isReservable(slot)) {
                                    <span
                                        class="booking-slot-availability-dot"
                                        aria-hidden="true"
                                    ></span>
                                } @else if (isWaitlistOnly(slot)) {
                                    <i class="bi bi-clock" aria-hidden="true"></i>
                                }
                                <span class="fw-semibold">
                                    {{ slot.bookingSlot.dateTime | date : 'shortTime' }}
                                </span>
                                @if (isWaitlistOnly(slot)) {
                                    <span class="visually-hidden">, join the waitlist</span>
                                } @else if (!isReservable(slot)) {
                                    <span class="visually-hidden">, unavailable</span>
                                }
                            </button>
                        </div>
                    }
                </div>

                <div
                    class="booking-slot-pagination d-grid align-items-center gap-3"
                    [attr.role]="totalPages() > 1 ? 'navigation' : null"
                    [attr.aria-label]="totalPages() > 1 ? 'Booking time pages' : null"
                >
                    @if (totalPages() > 1) {
                        <button
                            type="button"
                            class="booking-slot-pagination-button booking-slot-pagination-button--earlier btn px-3 py-2 btn-outline-secondary hstack gap-1"
                            data-testid="earlier-booking-slots"
                            [disabled]="currentPage() === 0"
                            (click)="previousPage()"
                        >
                            <img
                                ngSrc="assets/left/normal.svg"
                                width="16"
                                height="16"
                                alt=""
                                aria-hidden="true"
                            />
                            Earlier
                        </button>
                    }

                    <div
                        class="booking-slot-legend small text-secondary d-flex align-items-center justify-content-center gap-3 flex-wrap text-center"
                        data-testid="booking-slot-legend"
                    >
                        <span class="d-inline-flex align-items-center gap-2">
                            <span
                                class="booking-slot-availability-dot"
                                aria-hidden="true"
                            ></span>
                            Available
                        </span>
                        @if (hasWaitlistSlots()) {
                            <span
                                class="d-inline-flex align-items-center gap-1"
                                data-testid="waitlist-slots-explanation"
                            >
                                <i class="bi bi-clock" aria-hidden="true"></i>
                                Some times are fully booked, but you can join the waitlist
                            </span>
                        }
                    </div>

                    @if (totalPages() > 1) {
                        <button
                            type="button"
                            class="booking-slot-pagination-button booking-slot-pagination-button--later btn px-3 py-2 btn-outline-secondary hstack gap-1 justify-content-end"
                            data-testid="later-booking-slots"
                            [disabled]="currentPage() === totalPages() - 1"
                            (click)="nextPage()"
                        >
                            Later
                            <img
                                ngSrc="assets/right/normal.svg"
                                width="16"
                                height="16"
                                alt=""
                                aria-hidden="true"
                            />
                        </button>
                        <span class="visually-hidden" aria-live="polite">
                            Page {{ currentPage() + 1 }} of {{ totalPages() }}
                        </span>
                    }
                </div>
            }
        </div>
    }
</article>
